/**
 * <asd-welcome-popup>: the branded 10%-off popup (markup: snippets/asd-welcome-popup.liquid).
 *
 * Flow: offer -> form (email, optional phone) -> code.
 *  - Email is added to Shopify as a marketing subscriber through the storefront's own newsletter form.
 *  - A phone number (only when a connector address + Turnstile key are set) goes to the connector, which forwards it
 *    to Postscript. The connector holds the Postscript key; this file never sees it.
 *  - The discount code is applied to the visitor's cart with /discount/<code>.
 *
 * Suppression: after "No thanks" it stays away for N days; after signing up it never shows again. Not shown on cart,
 * checkout or account pages, to logged-in customers, or while another dialog (cart drawer, search...) is open.
 * QA: add ?asd-popup=1 to any page address to open it immediately and ignore the rules above.
 */

const STORAGE_KEY = 'asdWelcomePopup';
const SHOWN_KEY = 'asdWelcomePopupShown';
const BLOCKED_PATHS = /^\/(cart|checkout|checkouts|account|challenge|password)(\/|$)/i;
const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

const readState = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
};

const writeState = (patch) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...readState(), ...patch }));
  } catch {
    // private mode etc: the popup just won't remember
  }
};

const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** US / Canada numbers only: 10 digits (optionally with a leading 1) -> +1XXXXXXXXXX, otherwise ''. */
export function normalizePhone(input) {
  const digits = String(input || '').replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return '';
}

let turnstilePromise = null;
function loadTurnstile() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!turnstilePromise) {
    turnstilePromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = TURNSTILE_SRC;
      script.async = true;
      script.onload = () => resolve(window.turnstile);
      script.onerror = () => reject(new Error('Turnstile failed to load'));
      document.head.appendChild(script);
    });
  }
  return turnstilePromise;
}

class AsdWelcomePopup extends HTMLElement {
  /** @type {HTMLDialogElement | null} */
  #dialog = null;
  #busy = false;
  #timer = 0;
  #forced = false;
  #closing = false;
  /** @type {Element | null} */
  #returnFocus = null;
  #turnstileToken = '';
  #turnstileWidget = null;
  #turnstileStarted = false;
  #subscribed = false;

  connectedCallback() {
    this.#dialog = this.querySelector('[data-wp-dialog]');
    if (!this.#dialog) return;
    const dialog = this.#dialog;

    this.querySelectorAll('[data-wp-close]').forEach((el) => el.addEventListener('click', () => this.#close('dismiss')));
    this.querySelector('[data-wp-decline]')?.addEventListener('click', () => this.#close('decline'));
    this.querySelector('[data-wp-next]')?.addEventListener('click', () => this.#goto('form'));
    // Capture phase on the dialog: runs before Shopify's own form handler (which adds its bot-check token).
    dialog.addEventListener('submit', (event) => this.#onSubmit(event), true);
    this.querySelector('[data-wp-copy]')?.addEventListener('click', (event) => this.#copyCode(event.currentTarget));

    // Escape and clicks on the dark backdrop dismiss it (through our own animated close).
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.#close('dismiss');
    });
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) this.#close('dismiss');
    });
    dialog.addEventListener('close', () => {
      document.documentElement.style.overflow = '';
      this.#makePageInert(false);
      document.removeEventListener('keydown', this.#onKeydown);
      const focusTarget = this.#returnFocus;
      this.#returnFocus = null;
      if (focusTarget instanceof HTMLElement && document.contains(focusTarget)) focusTarget.focus({ preventScroll: true });
    });

    // Phone field: start the bot check the first time someone shows interest.
    this.querySelector('[data-wp-phone]')?.addEventListener('focus', () => this.#startTurnstile(), { once: true });

    this.#forced = new URLSearchParams(window.location.search).has('asd-popup');
    if (this.#forced) {
      this.#open();
      return;
    }
    if (this.dataset.mode !== 'on' || !this.#eligible()) return;

    const delay = Math.max(2, Number(this.dataset.delay) || 6) * 1000;
    this.#timer = window.setTimeout(() => this.#tryOpen(0), delay);
  }

  disconnectedCallback() {
    window.clearTimeout(this.#timer);
  }

  /** @type {HTMLElement[]} */
  #inerted = [];

  /** Escape closes it and Tab stays inside it, wherever focus happens to be. @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.#close('dismiss');
    } else if (event.key === 'Tab') {
      this.#loopFocus(event);
    }
  };

  /**
   * Makes the rest of the page unreachable while the popup is open (like a modal), without using the browser's top
   * layer. Elements added later (Shopify's bot-check puzzle) are not affected, so they stay usable.
   * @param {boolean} on
   */
  #makePageInert(on) {
    if (on) {
      this.#inerted = [...document.body.children].filter(
        (el) => el instanceof HTMLElement && el !== this && !el.contains(this) && !el.inert && el.tagName !== 'SCRIPT',
      );
      this.#inerted.forEach((el) => {
        el.inert = true;
      });
    } else {
      this.#inerted.forEach((el) => {
        el.inert = false;
      });
      this.#inerted = [];
    }
  }

  /** Keeps Tab / Shift+Tab cycling inside the popup. @param {KeyboardEvent} event */
  #loopFocus(event) {
    const focusable = [
      ...this.querySelectorAll('button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'),
    ].filter((el) => el instanceof HTMLElement && el.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && (document.activeElement === first || !this.contains(document.activeElement))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  #eligible() {
    if (BLOCKED_PATHS.test(window.location.pathname)) return false;
    const state = readState();
    if (state.subscribedAt) return false;
    if (state.snoozeUntil && Date.now() < state.snoozeUntil) return false;
    try {
      if (sessionStorage.getItem(SHOWN_KEY)) return false;
    } catch {
      // ignore
    }
    return true;
  }

  /** Wait politely if the visitor is busy (tab hidden, cart drawer or search open). */
  #tryOpen(attempt) {
    const busy = document.hidden || document.querySelector('dialog[open]');
    if (busy) {
      if (attempt < 8) this.#timer = window.setTimeout(() => this.#tryOpen(attempt + 1), 3000);
      return;
    }
    this.#open();
  }

  #open() {
    const dialog = this.#dialog;
    if (!dialog || dialog.open || typeof dialog.show !== 'function') return;
    this.#returnFocus = document.activeElement;
    try {
      sessionStorage.setItem(SHOWN_KEY, '1');
    } catch {
      // ignore
    }
    dialog.show(); // non-modal on purpose (see the CSS note about Shopify's bot-check puzzle)
    document.documentElement.style.overflow = 'hidden';
    this.#makePageInert(true);
    document.addEventListener('keydown', this.#onKeydown);
    this.#goto('offer', false);
    // Focus the heading: screen readers announce it, and Tab moves on to the button (no focus ring on load).
    this.querySelector('#asd-wp-title')?.focus({ preventScroll: true });
  }

  /** @param {'dismiss' | 'decline' | 'done'} reason */
  #close(reason) {
    const dialog = this.#dialog;
    if (!dialog || !dialog.open || this.#closing) return;
    this.#closing = true;

    if (!this.#forced && !this.#subscribed) {
      const days = Math.max(1, Number(this.dataset.snoozeDays) || 7);
      writeState({ snoozeUntil: Date.now() + days * 86400000 });
    }

    const finish = () => {
      if (!this.#closing) return;
      this.#closing = false;
      dialog.removeAttribute('data-closing');
      dialog.close();
    };
    if (prefersReducedMotion()) {
      finish();
      return;
    }
    dialog.setAttribute('data-closing', '');
    dialog.addEventListener('animationend', finish, { once: true });
    window.setTimeout(finish, 450); // fallback if animationend never fires
  }

  /** @param {'offer' | 'form' | 'done'} step @param {boolean} [focus] */
  #goto(step, focus = true) {
    this.querySelectorAll('[data-wp-step]').forEach((el) => {
      el.hidden = el.getAttribute('data-wp-step') !== step;
    });
    if (!focus) return;
    const target =
      step === 'form' ? this.querySelector('[data-wp-email]') : this.querySelector(`[data-wp-step="${step}"] h2`);
    if (target instanceof HTMLElement) target.focus({ preventScroll: true });
    this.#dialog?.scrollTo({ top: 0 });
  }

  /** @param {string} message */
  #showError(message) {
    const el = this.querySelector('[data-wp-error]');
    if (!el) return;
    el.textContent = message;
    el.hidden = !message;
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    this.#busy = busy;
    const button = this.querySelector('[data-wp-submit]');
    if (button instanceof HTMLButtonElement) {
      button.disabled = busy;
      if (busy) button.dataset.label = button.textContent ?? '';
      button.textContent = busy ? 'Unlocking...' : (button.dataset.label ?? button.textContent);
    }
  }

  async #startTurnstile() {
    if (this.#turnstileStarted || this.dataset.phone !== 'true') return;
    this.#turnstileStarted = true;
    try {
      const turnstile = await loadTurnstile();
      const container = this.querySelector('[data-wp-turnstile]');
      if (!turnstile || !container) return;
      this.#turnstileWidget = turnstile.render(container, {
        sitekey: this.dataset.turnstileKey,
        appearance: 'interaction-only', // invisible unless Cloudflare needs the visitor to prove they're human
        'response-field': false, // keep Turnstile's hidden field out of the Shopify form
        callback: (token) => {
          this.#turnstileToken = token;
        },
        'expired-callback': () => {
          this.#turnstileToken = '';
        },
        'error-callback': () => {
          this.#turnstileToken = '';
        },
      });
    } catch {
      this.#turnstileStarted = false;
    }
  }

  async #waitForToken(timeoutMs) {
    await this.#startTurnstile();
    const start = Date.now();
    while (!this.#turnstileToken && Date.now() - start < timeoutMs) {
      await new Promise((resolve) => setTimeout(resolve, 200));
    }
    return this.#turnstileToken;
  }

  /**
   * Validates before anything is sent. Invalid: stop the event so Shopify's handler never sees it. Valid: let it
   * through (Shopify adds its bot-check and submits into the hidden frame) and run the rest of the sign-up.
   * @param {Event} event
   */
  #onSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-wp-form]')) return;

    const values = this.#busy ? null : this.#validate();
    if (!values) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    // set up the listener for Shopify's answer before the form is sent
    const emailResult = this.#awaitEmailResult();
    void this.#run(values, emailResult);
  }

  /** @returns {{ email: string, phone: string } | null} */
  #validate() {
    this.#showError('');
    const emailInput = this.querySelector('[data-wp-email]');
    const phoneInput = this.querySelector('[data-wp-phone]');
    const consent = this.querySelector('[data-wp-sms-consent]');
    if (!(emailInput instanceof HTMLInputElement)) return null;

    const email = emailInput.value.trim();
    emailInput.setAttribute('aria-invalid', 'false');
    phoneInput?.setAttribute('aria-invalid', 'false');

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      emailInput.setAttribute('aria-invalid', 'true');
      this.#showError('Enter a valid email address.');
      emailInput.focus();
      return null;
    }

    let phone = '';
    if (phoneInput instanceof HTMLInputElement && phoneInput.value.trim()) {
      phone = normalizePhone(phoneInput.value);
      if (!phone) {
        phoneInput.setAttribute('aria-invalid', 'true');
        this.#showError('Enter a 10-digit US or Canadian mobile number, or leave it blank.');
        phoneInput.focus();
        return null;
      }
      if (!(consent instanceof HTMLInputElement) || !consent.checked) {
        this.#showError('Tick the box to agree to text messages, or clear the number.');
        consent?.focus();
        return null;
      }
    }
    return { email, phone };
  }

  /**
   * @param {{ email: string, phone: string }} values
   * @param {Promise<boolean>} emailResult
   */
  async #run({ email, phone }, emailResult) {
    this.#setBusy(true);
    try {
      let smsToken = '';
      if (phone) {
        smsToken = await this.#waitForToken(9000);
        if (!smsToken) {
          this.#showError('We could not verify the form. Please try again.');
          return;
        }
      }

      const [emailSettled, smsSettled] = await Promise.allSettled([
        emailResult,
        phone ? this.#subscribePhone(phone, email, smsToken) : Promise.resolve(null),
      ]);
      const emailOk = emailSettled.status === 'fulfilled' && emailSettled.value === true;
      const smsOk = smsSettled.status === 'fulfilled' && smsSettled.value === true;

      if (!emailOk && !smsOk) {
        this.#showError('Something went wrong. Please try again in a moment.');
        return;
      }

      await this.#applyCode();
      this.#subscribed = true;
      writeState({ subscribedAt: Date.now() });

      const note = this.querySelector('[data-wp-sms-note]');
      if (note) {
        if (phone && smsOk) {
          note.textContent = 'Check your texts: reply to our message to confirm your number and start getting drop alerts.';
          note.hidden = false;
        } else if (phone && !smsOk) {
          note.textContent = "We couldn't add your number just now, but your email and code are saved.";
          note.hidden = false;
        }
      }
      this.#goto('done');
    } finally {
      this.#setBusy(false);
      if (this.#turnstileWidget !== null && window.turnstile) {
        try {
          window.turnstile.reset(this.#turnstileWidget);
        } catch {
          // ignore
        }
        this.#turnstileToken = '';
      }
    }
  }

  /**
   * Resolves true once Shopify has accepted the sign-up. The form posts into a hidden frame, so we read where that
   * frame ended up: "customer_posted=true" means the subscriber was created; a challenge or error page means it wasn't.
   * @returns {Promise<boolean>}
   */
  #awaitEmailResult() {
    const frame = this.querySelector('[data-wp-frame]');
    if (!(frame instanceof HTMLIFrameElement)) return Promise.resolve(false);
    return new Promise((resolve) => {
      const timeout = window.setTimeout(() => {
        frame.removeEventListener('load', onLoad);
        resolve(false);
      }, 30000);
      const onLoad = () => {
        window.clearTimeout(timeout);
        try {
          const href = frame.contentWindow?.location.href ?? '';
          resolve(/customer_posted=true/.test(href) && !/\/challenge/.test(href));
        } catch {
          resolve(false);
        }
      };
      frame.addEventListener('load', onLoad, { once: true });
    });
  }

  /** Sends the phone number to the connector (which holds the Postscript key). */
  async #subscribePhone(phone, email, token) {
    const connector = this.dataset.connector;
    if (!connector) return false;
    const response = await fetch(connector, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        phone,
        email,
        consent: true,
        turnstileToken: token,
        source: 'welcome-popup',
        page: window.location.pathname,
      }),
    });
    if (!response.ok) return false;
    const data = await response.json().catch(() => ({}));
    return data.ok === true;
  }

  /** Puts the discount code on the visitor's cart so checkout applies it without typing. */
  async #applyCode() {
    const code = this.dataset.code;
    if (!code) return;
    try {
      await fetch(`/discount/${encodeURIComponent(code)}`, { credentials: 'same-origin', redirect: 'manual' });
    } catch {
      // the code is still shown on screen for them to type
    }
  }

  /** @param {EventTarget | null} button */
  async #copyCode(button) {
    const code = this.dataset.code || '';
    if (!code || !(button instanceof HTMLElement)) return;
    let ok = false;
    try {
      await navigator.clipboard.writeText(code);
      ok = true;
    } catch {
      const el = this.querySelector('[data-wp-code]');
      if (el) {
        const range = document.createRange();
        range.selectNodeContents(el);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
        try {
          ok = document.execCommand('copy');
        } catch {
          ok = false;
        }
      }
    }
    const original = button.textContent;
    button.textContent = ok ? 'Copied' : 'Select + copy';
    window.setTimeout(() => {
      button.textContent = original;
    }, 1800);
  }
}

if (!customElements.get('asd-welcome-popup')) {
  customElements.define('asd-welcome-popup', AsdWelcomePopup);
}
